import { useState, useCallback, useRef } from "react";
import { T, Badge, Card, Btn, SectionTitle, Input, Select, DataTable, StatCard, fmtCcy, font, mono, typeColor, statusColorFn } from '../theme.js';
import { AccountType, AccountStatus, Direction } from '../engines/ledger-engine.js';

const ST = SectionTitle;
const Sel = Select;

const ACCOUNTS_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: '◉' },
  { id: 'accounts', label: 'Chart of Accounts', icon: '⊞' },
  { id: 'transfer', label: 'Post Transaction', icon: '↹' },
  { id: 'entries', label: 'Entry Log', icon: '≡' },
  { id: 'trial', label: 'Trial Balance', icon: '⊟' },
  { id: 'events', label: 'Events', icon: '⚡' },
  { id: 'integrity', label: 'Integrity Check', icon: '✓' },
  { id: 'interest', label: 'Interest', icon: '∿' },
  { id: 'eod', label: 'End of Day', icon: '⏱' },
  { id: 'statements', label: 'Statements', icon: '☰' },
  { id: 'products', label: 'Products', icon: '⚙' },
];

export default function AccountsModule({ ledger, eventBus, eod, refresh: parentRefresh }) {
  const [view, setView] = useState('dashboard');
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);

  const refresh = useCallback(() => {
    setTick(t => t + 1);
    if (parentRefresh) parentRefresh();
  }, [parentRefresh]);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  const showError = useCallback(msg => showToast(msg, 'error'), [showToast]);

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000, padding: '10px 16px',
          background: toast.type === 'error' ? T.redBg : T.greenBg,
          border: `1px solid ${toast.type === 'error' ? T.red : T.green}40`,
          borderRadius: 6, fontSize: 12, maxWidth: 420, fontFamily: font }}>
          <span style={{ color: toast.type === 'error' ? T.red : T.green, marginRight: 8 }}>{toast.type === 'error' ? '✗' : '✓'}</span>{toast.msg}
        </div>
      )}

      {/* Sub-navigation tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {ACCOUNTS_NAV.map(n => (
          <button key={n.id} onClick={() => setView(n.id)} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: view === n.id ? 600 : 400,
            color: view === n.id ? T.blue : T.textSecondary,
            background: 'transparent', border: 'none',
            borderBottom: view === n.id ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1,
            whiteSpace: 'nowrap',
          }}>
            <span style={{ marginRight: 5 }}>{n.icon}</span>{n.label}
          </button>
        ))}
      </div>

      {/* Views */}
      {view === 'dashboard' && <DashboardView ledger={ledger} key={tick} />}
      {view === 'accounts' && <AccountsView ledger={ledger} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'transfer' && <TransferView ledger={ledger} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'entries' && <EntriesView ledger={ledger} key={tick} />}
      {view === 'trial' && <TrialBalanceView ledger={ledger} key={tick} />}
      {view === 'events' && <EventsView eventBus={eventBus} key={tick} />}
      {view === 'integrity' && <IntegrityView ledger={ledger} key={tick} />}
      {view === 'interest' && <InterestView ledger={ledger} eod={eod} refresh={refresh} showToast={showToast} key={tick} />}
      {view === 'eod' && <EODView ledger={ledger} eod={eod} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'statements' && <StatementsView ledger={ledger} eod={eod} key={tick} />}
      {view === 'products' && <ProductsView key={tick} />}
    </div>
  );
}


// ============================================================
// DASHBOARD
// ============================================================

function DashboardView({ ledger }) {
  const integrity = ledger.verifyIntegrity();
  const allAccs = ledger.getAllAccounts();
  const custAccs = allAccs.filter(a => !a.metadata?.internal);
  const activeAccs = custAccs.filter(a => a.status === AccountStatus.ACTIVE);
  const totalDeposits = activeAccs.reduce((s, a) => a.type === AccountType.LIABILITY ? s + ledger.getBalance(a.id).posted : s, 0);
  const intAccs = allAccs.filter(a => a.metadata?.internal);

  return (
    <div>
      <ST>Ledger Overview</ST>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Customer Accounts" value={custAccs.length} sub={`${activeAccs.length} active`} color={T.blue} />
        <StatCard label="Total Deposits" value={fmtCcy(totalDeposits)} color={T.green} />
        <StatCard label="Internal Accounts" value={intAccs.length} color={T.purple} />
        <StatCard label="Transactions" value={integrity.stats.transactions} sub={`${integrity.stats.entries} entries`} color={T.text} />
        <StatCard label="Integrity" value={integrity.valid ? 'VALID' : 'ERROR'} color={integrity.valid ? T.green : T.red} />
      </div>

      <Card>
        <ST>Recent Transactions</ST>
        {ledger.transactions.size === 0
          ? <div style={{ color: T.textMuted, fontSize: 13, padding: 24, textAlign: 'center' }}>No transactions yet. Seed data will be loaded by the shell.</div>
          : <DataTable compact
              headers={['Ref', 'Type', 'Entries', 'Time', 'By']}
              rows={Array.from(ledger.transactions.values()).slice(-12).reverse().map(t => [
                <span style={{ fontSize: 11 }}>{t.reference.length > 28 ? t.reference.slice(0, 28) + '…' : t.reference}</span>,
                <Badge color={t.type.includes('FEE') ? T.amber : t.type.includes('INTEREST') ? T.cyan : t.type.includes('DEPOSIT') ? T.green : T.blue}>{t.type}</Badge>,
                <div>{t.entries.map((e, i) => {
                  const acc = ledger.accounts.get(e.accountId);
                  const name = acc?.accountNumber || '?';
                  return <div key={i} style={{ fontSize: 11, color: e.direction === Direction.DEBIT ? T.red : T.green, whiteSpace: 'nowrap' }}>
                    {e.direction === Direction.DEBIT ? 'DR' : 'CR'} {name.length > 22 ? name.slice(0, 22) + '…' : name} {fmtCcy(e.amount, e.currency)}
                  </div>;
                })}</div>,
                <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(t.createdAt).toLocaleTimeString()}</span>,
                <span style={{ fontSize: 11, color: T.textSecondary }}>{t.createdBy}</span>,
              ])}
            />
        }
      </Card>
    </div>
  );
}


// ============================================================
// CHART OF ACCOUNTS
// ============================================================

function AccountsView({ ledger, refresh, showToast, showError }) {
  const [showInternal, setShowInternal] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ accountNumber: '', name: '', type: AccountType.LIABILITY, currency: 'GBP', customerId: '' });

  const accounts = ledger.getAllAccounts({ internal: showInternal ? undefined : false });

  const handleCreate = async () => {
    try {
      await ledger.createAccount({ ...form, metadata: { internal: false } });
      await refresh(); showToast(`Account ${form.accountNumber} created`);
      setShowCreate(false);
    } catch (e) { showError(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Chart of Accounts</ST>
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn variant="secondary" small onClick={() => setShowInternal(!showInternal)}>{showInternal ? 'Hide Internal' : 'Show Internal'}</Btn>
          <Btn small onClick={() => setShowCreate(!showCreate)}>+ New Account</Btn>
        </div>
      </div>

      {showCreate && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Create Account</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <Input label="Account Number" value={form.accountNumber} onChange={v => setForm({ ...form, accountNumber: v })} placeholder="ACME-001" />
            <Input label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="Acme Corp Current" />
            <Sel label="Type" value={form.type} onChange={v => setForm({ ...form, type: v })} options={Object.values(AccountType).map(t => ({ value: t, label: t }))} />
            <Sel label="Currency" value={form.currency} onChange={v => setForm({ ...form, currency: v })} options={[{ value: 'GBP', label: 'GBP' }, { value: 'USD', label: 'USD' }, { value: 'EUR', label: 'EUR' }]} />
            <Input label="Customer ID" value={form.customerId} onChange={v => setForm({ ...form, customerId: v })} placeholder="cust_acme" />
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 6 }}><Btn small onClick={handleCreate}>Create</Btn><Btn variant="secondary" small onClick={() => setShowCreate(false)}>Cancel</Btn></div>
        </Card>
      )}

      <Card>
        <DataTable
          headers={['Account', 'Name', 'Type', 'CCY', 'Status', 'Balance', 'Available', '']}
          rows={accounts.map(a => {
            const bal = ledger.getBalance(a.id);
            return [
              <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{a.accountNumber}</span>,
              <span style={{ fontSize: 12 }}>{a.name}</span>,
              <Badge color={typeColor(a.type)}>{a.type}</Badge>,
              <span>{a.currency}</span>,
              <Badge color={statusColorFn(a.status)}>{a.status}</Badge>,
              <span style={{ fontWeight: 600, fontFamily: mono, color: bal.posted < 0 ? T.red : T.text }}>{fmtCcy(bal.posted, a.currency)}</span>,
              <span style={{ fontSize: 11, fontFamily: mono, color: T.textMuted }}>{fmtCcy(bal.available, a.currency)}</span>,
              a.status === AccountStatus.PENDING
                ? <Btn variant="secondary" small onClick={async () => { await ledger.transitionAccountStatus(a.id, AccountStatus.ACTIVE, 'user', 'Activated'); await refresh(); showToast('Activated'); }}>Activate</Btn>
                : null,
            ];
          })}
        />
      </Card>
    </div>
  );
}


// ============================================================
// POST TRANSACTION
// ============================================================

function TransferView({ ledger, refresh, showToast, showError }) {
  const activeAccs = Array.from(ledger.accounts.values()).filter(a => a.status === AccountStatus.ACTIVE);
  const [form, setForm] = useState({ type: 'BOOK_TRANSFER', reference: '', fromId: '', toId: '', amount: '', currency: 'GBP', createdBy: 'rm_user' });
  const ccyAccs = activeAccs.filter(a => a.currency === form.currency);

  const handlePost = async () => {
    try {
      const amount = parseFloat(form.amount);
      if (isNaN(amount) || amount <= 0) throw new Error('Invalid amount');
      if (!form.fromId || !form.toId) throw new Error('Select both accounts');
      if (form.fromId === form.toId) throw new Error('Same account');
      await ledger.postTransaction({
        type: form.type, reference: form.reference || `TXN-${Date.now()}`,
        legs: [
          { accountId: form.fromId, amount, direction: Direction.DEBIT, currency: form.currency },
          { accountId: form.toId, amount, direction: Direction.CREDIT, currency: form.currency },
        ],
        createdBy: form.createdBy,
      });
      await refresh(); showToast(`Posted: ${fmtCcy(amount, form.currency)}`);
      setForm({ ...form, reference: '', amount: '' });
    } catch (e) { showError(e.message); }
  };

  return (
    <div>
      <ST>Post Transaction</ST>
      <Card style={{ maxWidth: 560 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Sel label="Type" value={form.type} onChange={v => setForm({ ...form, type: v })} options={[
            { value: 'BOOK_TRANSFER', label: 'Book Transfer' }, { value: 'FPS_INBOUND', label: 'FPS Inbound' },
            { value: 'FPS_OUTBOUND', label: 'FPS Outbound' }, { value: 'FEE', label: 'Fee Charge' },
            { value: 'ADJUSTMENT', label: 'Adjustment' },
          ]} />
          <Sel label="Currency" value={form.currency} onChange={v => setForm({ ...form, currency: v })} options={[{ value: 'GBP', label: 'GBP' }, { value: 'USD', label: 'USD' }, { value: 'EUR', label: 'EUR' }]} />
        </div>
        <Input label="Reference" value={form.reference} onChange={v => setForm({ ...form, reference: v })} placeholder="FPS-OUT-002" />
        <Sel label="Debit (From)" value={form.fromId} onChange={v => setForm({ ...form, fromId: v })}
          options={[{ value: '', label: 'Select...' }, ...ccyAccs.map(a => { const b = ledger.getBalance(a.id); return { value: a.id, label: `${a.accountNumber} (${fmtCcy(b.posted, a.currency)})` }; })]} />
        <Sel label="Credit (To)" value={form.toId} onChange={v => setForm({ ...form, toId: v })}
          options={[{ value: '', label: 'Select...' }, ...ccyAccs.map(a => { const b = ledger.getBalance(a.id); return { value: a.id, label: `${a.accountNumber} (${fmtCcy(b.posted, a.currency)})` }; })]} />
        <Input label="Amount" type="number" value={form.amount} onChange={v => setForm({ ...form, amount: v })} placeholder="0.00" />
        <Btn onClick={handlePost}>Post Transaction</Btn>
      </Card>
    </div>
  );
}


// ============================================================
// ENTRY LOG
// ============================================================

function EntriesView({ ledger }) {
  const entries = [...ledger.entries].reverse().slice(0, 80);
  return (
    <div>
      <ST>Immutable Entry Log</ST>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>Append-only. Never modified or deleted. Showing {entries.length} of {ledger.entries.length}.</div>
      <Card>
        <DataTable compact
          headers={['ID', 'Txn', 'Account', 'Dir', 'Amount', 'Posted']}
          rows={entries.map(e => {
            const acc = ledger.accounts.get(e.accountId);
            return [
              <span style={{ fontSize: 10, color: T.textMuted, fontFamily: mono }}>{e.id.slice(0, 14)}…</span>,
              <span style={{ fontSize: 10, fontFamily: mono }}>{e.transactionId.slice(0, 10)}…</span>,
              <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{acc?.accountNumber || '?'}</span>,
              <Badge color={e.direction === Direction.DEBIT ? T.red : T.green}>{e.direction}</Badge>,
              <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(e.amount, e.currency)}</span>,
              <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(e.postedAt).toLocaleString()}</span>,
            ];
          })}
        />
      </Card>
    </div>
  );
}


// ============================================================
// TRIAL BALANCE
// ============================================================

function TrialBalanceView({ ledger }) {
  const [ccy, setCcy] = useState('GBP');
  const tb = ledger.getTrialBalance(ccy);
  const totalD = tb.reduce((s, r) => s + r.debitBalance, 0);
  const totalC = tb.reduce((s, r) => s + r.creditBalance, 0);
  const balanced = Math.abs(totalD - totalC) < 0.01;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Trial Balance</ST>
        <div style={{ display: 'flex', gap: 6 }}>{['GBP', 'USD', 'EUR'].map(c => <Btn key={c} variant={ccy === c ? 'primary' : 'secondary'} small onClick={() => setCcy(c)}>{c}</Btn>)}</div>
      </div>
      <Card>
        <DataTable compact headers={['Account', 'Name', 'Type', 'Debit', 'Credit']} rows={[
          ...tb.map(r => [
            <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{r.accountNumber}</span>,
            <span style={{ fontSize: 12 }}>{r.name}</span>,
            <Badge color={typeColor(r.type)}>{r.type}</Badge>,
            <span style={{ fontFamily: mono, color: r.debitBalance > 0 ? T.text : T.textMuted }}>{r.debitBalance > 0 ? fmtCcy(r.debitBalance, ccy) : '—'}</span>,
            <span style={{ fontFamily: mono, color: r.creditBalance > 0 ? T.text : T.textMuted }}>{r.creditBalance > 0 ? fmtCcy(r.creditBalance, ccy) : '—'}</span>,
          ]),
          ['', '', '',
            <span style={{ fontWeight: 700, fontFamily: mono, borderTop: `2px solid ${T.border}`, paddingTop: 6, display: 'inline-block' }}>{fmtCcy(totalD, ccy)}</span>,
            <span style={{ fontWeight: 700, fontFamily: mono, borderTop: `2px solid ${T.border}`, paddingTop: 6, display: 'inline-block' }}>{fmtCcy(totalC, ccy)}</span>,
          ],
        ]} />
        <div style={{ marginTop: 10, padding: 8, borderRadius: 6, background: balanced ? T.greenBg : T.redBg, border: `1px solid ${balanced ? T.greenBorder : T.red}`, fontSize: 12, textAlign: 'center', fontWeight: 600, color: balanced ? T.green : T.red }}>
          {balanced ? '✓ Trial balance is in balance' : `✗ Imbalance: ${fmtCcy(Math.abs(totalD - totalC), ccy)}`}
        </div>
      </Card>
    </div>
  );
}


// ============================================================
// EVENT STREAM
// ============================================================

function EventsView({ eventBus }) {
  const events = eventBus ? [...eventBus.eventLog].reverse().slice(0, 40) : [];
  return (
    <div>
      <ST>Event Stream</ST>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>Every state change produces an immutable event. In production: Kafka topics.</div>
      <Card>
        <div style={{ maxHeight: 500, overflow: 'auto' }}>
          {events.length === 0
            ? <div style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No events yet.</div>
            : events.map((e, i) => (
              <div key={i} style={{ padding: '8px 10px', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <Badge color={e.type.includes('Created') ? T.blue : e.type.includes('Posted') || e.type.includes('Payment') ? T.green : e.type.includes('Status') ? T.amber : T.textMuted}>{e.type}</Badge>
                  <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(e.timestamp).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: mono }}>{JSON.stringify(e.data).slice(0, 200)}</div>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}


// ============================================================
// INTEGRITY CHECK
// ============================================================

function IntegrityView({ ledger }) {
  const [result, setResult] = useState(null);

  const runCheck = () => {
    const r = ledger.verifyIntegrity();
    const recon = [];
    for (const [accId, acc] of ledger.accounts) {
      const mat = ledger.getBalance(accId).posted;
      const rec = ledger.reconstructBalance(accId);
      if (mat !== 0 || rec !== 0) recon.push({ accountNumber: acc.accountNumber, currency: acc.currency, materialised: mat, reconstructed: rec, match: Math.abs(mat - rec) < 0.001 });
    }
    setResult({ ...r, recon });
  };

  return (
    <div>
      <ST>Ledger Integrity Check</ST>
      <Btn onClick={runCheck} style={{ marginBottom: 16 }}>Run Full Check</Btn>

      {result && (
        <>
          <Card style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: result.valid ? T.greenBg : T.redBg, border: `2px solid ${result.valid ? T.green : T.red}`, fontSize: 20,
              }}>
                {result.valid ? '✓' : '✗'}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: result.valid ? T.green : T.red }}>{result.valid ? 'All Checks Passed' : 'Issues Found'}</div>
                <div style={{ fontSize: 12, color: T.textSecondary }}>{result.stats.accounts} accounts, {result.stats.entries} entries, {result.stats.transactions} txns</div>
              </div>
            </div>
            {result.issues.length > 0 && (
              <div style={{ background: T.redBg, border: `1px solid ${T.red}40`, borderRadius: 6, padding: 10 }}>
                {result.issues.map((issue, i) => <div key={i} style={{ fontSize: 12, color: T.red, marginBottom: 3 }}>• {issue}</div>)}
              </div>
            )}
          </Card>

          {result.recon.length > 0 && (
            <Card>
              <ST>Balance Reconstruction Proof</ST>
              <DataTable compact headers={['Account', 'Materialised', 'Reconstructed', 'Match']} rows={result.recon.map(r => [
                <span style={{ fontWeight: 600, fontFamily: mono }}>{r.accountNumber}</span>,
                <span style={{ fontFamily: mono }}>{fmtCcy(r.materialised, r.currency)}</span>,
                <span style={{ fontFamily: mono }}>{fmtCcy(r.reconstructed, r.currency)}</span>,
                <Badge color={r.match ? T.green : T.red}>{r.match ? 'MATCH' : 'MISMATCH'}</Badge>,
              ])} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ============================================================
// INTEREST VIEW — accrual status, daily calculations, tier breakdown
// ============================================================

function InterestView({ ledger, eod, refresh, showToast }) {
  const [calcResult, setCalcResult] = useState(null);

  if (!eod) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>EOD processor not available</div>;

  const ie = eod.interestEngine;
  const accruals = [];
  for (const [accId, acc] of ledger.accounts) {
    if (acc.metadata?.internal || acc.status !== 'ACTIVE' || !acc.metadata?.productId) continue;
    const accrual = ledger.accruals.get(accId);
    const bal = ledger.getBalance(accId);
    accruals.push({ acc, balance: bal.posted, accrual: accrual || { credit: 0, debit: 0, entries: [] } });
  }

  const runDailyCalc = async () => {
    const results = await ie.runDailyAccrual(new Date());
    await refresh();
    setCalcResult(results);
    showToast(`Daily accrual: ${results.length} calculation(s), total ${fmtCcy(results.reduce((s, r) => s + r.amount, 0))}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Interest Accrual</ST>
        <Btn onClick={runDailyCalc} small>Run Daily Accrual</Btn>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <ST>Accrual Status by Account</ST>
        <DataTable compact headers={['Account', 'Balance', 'Credit Accrued', 'OD Accrued', 'Entries']} rows={accruals.map(a => [
          <span style={{ fontWeight: 600, fontFamily: mono }}>{a.acc.accountNumber}</span>,
          <span style={{ fontFamily: mono, color: a.balance < 0 ? T.red : T.text }}>{fmtCcy(a.balance, a.acc.currency)}</span>,
          <span style={{ fontFamily: mono, color: T.green }}>{fmtCcy(a.accrual.credit)}</span>,
          <span style={{ fontFamily: mono, color: a.accrual.debit > 0 ? T.red : T.textMuted }}>{fmtCcy(a.accrual.debit)}</span>,
          <span style={{ color: T.textMuted }}>{a.accrual.entries.length}</span>,
        ])} />
      </Card>

      {calcResult && calcResult.length > 0 && (
        <Card>
          <ST>Last Accrual Run</ST>
          {calcResult.map((r, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{r.type === 'credit' ? 'Credit Interest' : 'Overdraft Interest'}</span>
                <span style={{ fontFamily: mono, fontWeight: 700, color: r.type === 'credit' ? T.green : T.red }}>{fmtCcy(r.amount)}</span>
              </div>
              <div style={{ color: T.textMuted, fontSize: 11 }}>
                Balance: {fmtCcy(r.balance || r.overdrawnAmount)} · Method: {r.method || 'ACT/365'} · Date: {r.date}
              </div>
              {r.tiers && r.tiers.length > 0 && (
                <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: `2px solid ${T.borderLight}`, fontSize: 11, color: T.textSecondary }}>
                  {r.tiers.map((t, j) => (
                    <div key={j}>{fmtCcy(t.amountInTier)} @ {(t.rate * 100).toFixed(2)}% → {fmtCcy(t.dailyInterest)}/day</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}


// ============================================================
// END OF DAY VIEW — run EOD batch, view logs
// ============================================================

function EODView({ ledger, eod, refresh, showToast, showError }) {
  const [lastLog, setLastLog] = useState(null);
  const [monthEnd, setMonthEnd] = useState(false);

  if (!eod) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>EOD processor not available</div>;

  const runEOD = async () => {
    try {
      const log = await Promise.resolve(eod.runEOD(new Date(), { monthEnd }));
      setLastLog(log);
      refresh();
      showToast(`EOD complete: ${log.steps.length} steps processed`);
    } catch (e) { showError(e.message); }
  };

  const logs = eod.eodLog || [];

  return (
    <div>
      <ST>End of Day Processing</ST>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Btn onClick={runEOD}>Run EOD Batch</Btn>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSecondary, cursor: 'pointer' }}>
            <input type="checkbox" checked={monthEnd} onChange={e => setMonthEnd(e.target.checked)} />
            Include month-end (interest payment, OD charges, fees)
          </label>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: T.textMuted }}>
          Sequence: Expire Holds → Interest Accrual{monthEnd ? ' → Interest Payment → OD Charges → Monthly Fees' : ''} → Balance Snapshot
        </div>
      </Card>

      {lastLog && (
        <Card style={{ marginBottom: 16, borderLeft: `3px solid ${T.green}` }}>
          <ST>Last Run: {lastLog.date}</ST>
          {lastLog.steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
              <span style={{ fontWeight: 500 }}>{s.step}</span>
              <span style={{ fontFamily: mono, color: T.textSecondary }}>
                {s.count !== undefined ? `${s.count} processed` : s.accountCount !== undefined ? `${s.accountCount} accounts` : ''}
                {s.totalAccrued ? ` · ${fmtCcy(s.totalAccrued)}` : ''}
                {s.totalPaid ? ` · ${fmtCcy(s.totalPaid)}` : ''}
                {s.totalCharged ? ` · ${fmtCcy(s.totalCharged)}` : ''}
                {s.totalFees ? ` · ${fmtCcy(s.totalFees)}` : ''}
              </span>
            </div>
          ))}
        </Card>
      )}

      {logs.length > 0 && (
        <Card>
          <ST>EOD History ({logs.length} runs)</ST>
          <DataTable compact headers={['Date', 'Steps', 'Completed']} rows={[...logs].reverse().slice(0, 20).map(l => [
            <span style={{ fontFamily: mono, fontWeight: 600 }}>{l.date}</span>,
            <span style={{ color: T.textSecondary }}>{l.steps.map(s => s.step).join(' → ')}</span>,
            <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(l.completedAt).toLocaleTimeString()}</span>,
          ])} />
        </Card>
      )}
    </div>
  );
}


// ============================================================
// STATEMENTS VIEW — generate and view account statements
// ============================================================

function StatementsView({ ledger, eod }) {
  const [selAccount, setSelAccount] = useState('');
  const [statement, setStatement] = useState(null);

  if (!eod) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>EOD processor not available</div>;

  const custAccs = ledger.getAllAccounts({ internal: false }).filter(a => a.status === 'ACTIVE');
  const se = eod.statementEngine;

  const generate = async () => {
    if (!selAccount) return;
    // Use full time range to capture all entries
    const earliest = ledger.entries[0]?.postedAt || new Date().toISOString();
    const now = new Date().toISOString();
    const stmt = await se.generateStatement(selAccount, earliest, now);
    setStatement(stmt);
  };

  return (
    <div>
      <ST>Account Statements</ST>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Sel label="Account" value={selAccount} onChange={v => setSelAccount(v)}
              options={[{ value: '', label: 'Select account...' }, ...custAccs.map(a => ({ value: a.id, label: `${a.accountNumber} — ${a.name}` }))]} />
          </div>
          <Btn onClick={generate} disabled={!selAccount} style={{ marginBottom: 10 }}>Generate Statement</Btn>
        </div>
      </Card>

      {statement && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{statement.accountName}</div>
              <div style={{ fontSize: 12, color: T.textMuted, fontFamily: mono }}>{statement.accountNumber} · {statement.currency}</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12 }}>
              <div style={{ color: T.textMuted }}>Statement Period</div>
              <div style={{ fontFamily: mono }}>{new Date(statement.periodFrom).toLocaleDateString('en-GB')} — {new Date(statement.periodTo).toLocaleDateString('en-GB')}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <StatCard label="Opening" value={fmtCcy(statement.openingBalance, statement.currency)} />
            <StatCard label="Credits" value={fmtCcy(statement.totalCredits, statement.currency)} color={T.green} />
            <StatCard label="Debits" value={fmtCcy(statement.totalDebits, statement.currency)} color={T.red} />
            <StatCard label="Closing" value={fmtCcy(statement.closingBalance, statement.currency)} />
          </div>

          {statement.accruedInterest && (statement.accruedInterest.credit > 0 || statement.accruedInterest.overdraft > 0) && (
            <div style={{ padding: '8px 12px', background: T.surfaceAlt, borderRadius: 6, marginBottom: 12, fontSize: 12, display: 'flex', gap: 16 }}>
              <span style={{ color: T.textMuted }}>Accrued Interest:</span>
              {statement.accruedInterest.credit > 0 && <span style={{ color: T.green }}>Credit: {fmtCcy(statement.accruedInterest.credit)}</span>}
              {statement.accruedInterest.overdraft > 0 && <span style={{ color: T.red }}>Overdraft: {fmtCcy(statement.accruedInterest.overdraft)}</span>}
            </div>
          )}

          <DataTable compact headers={['Date', 'Reference', 'Type', 'Debit', 'Credit', 'Balance']} rows={statement.lines.map(l => [
            <span style={{ fontSize: 10, fontFamily: mono, color: T.textMuted }}>{new Date(l.date).toLocaleDateString('en-GB')}</span>,
            <span style={{ fontFamily: mono, fontSize: 11 }}>{l.reference}</span>,
            <span style={{ color: T.textSecondary, fontSize: 11 }}>{l.type}</span>,
            <span style={{ fontFamily: mono, color: T.red }}>{l.debit ? fmtCcy(l.debit, statement.currency) : ''}</span>,
            <span style={{ fontFamily: mono, color: T.green }}>{l.credit ? fmtCcy(l.credit, statement.currency) : ''}</span>,
            <span style={{ fontFamily: mono, fontWeight: 600 }}>{fmtCcy(l.balance, statement.currency)}</span>,
          ])} />

          <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted, textAlign: 'right' }}>
            Generated: {new Date(statement.generatedAt).toLocaleString()} · {statement.entryCount} entries
          </div>
        </Card>
      )}

      {se.statements.length > 0 && !statement && (
        <Card>
          <ST>Generated Statements ({se.statements.length})</ST>
          <DataTable compact headers={['Account', 'Period', 'Entries', 'Closing']} rows={[...se.statements].reverse().map(s => [
            <span style={{ fontFamily: mono, fontWeight: 600 }}>{s.accountNumber}</span>,
            <span style={{ fontSize: 11, color: T.textSecondary }}>{new Date(s.periodFrom).toLocaleDateString('en-GB')} — {new Date(s.periodTo).toLocaleDateString('en-GB')}</span>,
            <span style={{ fontFamily: mono }}>{s.entryCount}</span>,
            <span style={{ fontFamily: mono, fontWeight: 600 }}>{fmtCcy(s.closingBalance, s.currency)}</span>,
          ])} />
        </Card>
      )}
    </div>
  );
}


// ============================================================
// PRODUCTS VIEW — product template catalog
// ============================================================

const PRODUCT_CATALOG = {
  COMMERCIAL_CURRENT_GBP: { id: 'COMMERCIAL_CURRENT_GBP', name: 'Commercial Current Account (GBP)', currency: 'GBP', interestBasis: 'DAILY_ACCRUAL', interestMethod: 'ACT/365', tiers: [{ from: 0, to: 50000, rate: 0.0025 }, { from: 50000, to: 250000, rate: 0.005 }, { from: 250000, to: Infinity, rate: 0.0075 }], overdraft: true, odRate: 0.089, odMax: 125000, fees: [{ type: 'Maintenance', amount: 25, freq: 'Monthly' }, { type: 'FPS', amount: 0.20, freq: 'Per Event' }, { type: 'CHAPS', amount: 25, freq: 'Per Event' }, { type: 'International', amount: 15, freq: 'Per Event' }], limits: { daily: 250000, single: 100000 } },
  COMMERCIAL_CURRENT_USD: { id: 'COMMERCIAL_CURRENT_USD', name: 'Commercial Current Account (USD)', currency: 'USD', interestBasis: 'DAILY_ACCRUAL', interestMethod: 'ACT/360', tiers: [{ from: 0, to: 100000, rate: 0.003 }, { from: 100000, to: Infinity, rate: 0.005 }], overdraft: false, fees: [{ type: 'Maintenance', amount: 30, freq: 'Monthly' }, { type: 'International', amount: 12, freq: 'Per Event' }], limits: { daily: 500000, single: 250000 } },
  COMMERCIAL_CURRENT_EUR: { id: 'COMMERCIAL_CURRENT_EUR', name: 'Commercial Current Account (EUR)', currency: 'EUR', interestBasis: 'NONE', tiers: [], overdraft: false, fees: [{ type: 'Maintenance', amount: 20, freq: 'Monthly' }], limits: { daily: 200000, single: 100000 } },
  DEPOSIT_ACCOUNT_GBP: { id: 'DEPOSIT_ACCOUNT_GBP', name: 'Fixed Term Deposit (GBP)', currency: 'GBP', interestBasis: 'DAILY_ACCRUAL', interestMethod: 'ACT/365', tiers: [{ from: 0, to: Infinity, rate: 0.035 }], overdraft: false, fees: [], limits: { daily: 0, single: 0 } },
};

function ProductsView() {
  const products = Object.values(PRODUCT_CATALOG);

  return (
    <div>
      <ST>Product Catalog</ST>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {products.map(p => (
          <Card key={p.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: mono }}>{p.id}</div>
              </div>
              <Badge color={typeColor(p.currency === 'GBP' ? 'ASSET' : p.currency === 'USD' ? 'LIABILITY' : 'INCOME')}>{p.currency}</Badge>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, fontSize: 12, marginBottom: 12 }}>
              <div>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Interest</div>
                {p.tiers.length > 0 ? p.tiers.map((t, i) => (
                  <div key={i} style={{ color: T.textSecondary }}>
                    {fmtCcy(t.from)} — {t.to === Infinity ? '∞' : fmtCcy(t.to)}: <span style={{ fontWeight: 600 }}>{(t.rate * 100).toFixed(2)}%</span>
                  </div>
                )) : <div style={{ color: T.textMuted }}>None</div>}
                {p.interestMethod && <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>{p.interestMethod}</div>}
              </div>

              <div>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Overdraft</div>
                {p.overdraft
                  ? <div style={{ color: T.textSecondary }}>Max: {fmtCcy(p.odMax || 0)} @ <span style={{ fontWeight: 600 }}>{((p.odRate || 0) * 100).toFixed(1)}%</span></div>
                  : <div style={{ color: T.textMuted }}>Not available</div>}
              </div>

              <div>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Limits</div>
                {p.limits && <div style={{ color: T.textSecondary }}>Daily: {fmtCcy(p.limits.daily || 0)} · Single: {fmtCcy(p.limits.single || 0)}</div>}
              </div>
            </div>

            {p.fees.length > 0 && (
              <div style={{ borderTop: `1px solid ${T.borderLight}`, paddingTop: 8 }}>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Fees</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                  {p.fees.map((f, i) => (
                    <span key={i} style={{ color: T.textSecondary }}>{f.type}: <span style={{ fontWeight: 600 }}>{fmtCcy(f.amount, p.currency)}</span> <span style={{ fontSize: 10, color: T.textMuted }}>({f.freq})</span></span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}


// ============================================================
